/**
 * Geolocator — self-contained web component.
 * Two-view popup:
 *   1) Info view — confirms current location
 *   2) Select view — searchable country picker
 */

/* ── Utilities ───────────────────────────────────────────────────── */

function handleize(str) {
  return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

function getCookie(name) {
  const prefix = `${name}=`;
  for (let c of document.cookie.split(';')) {
    c = c.trim();
    if (c.startsWith(prefix)) return c.substring(prefix.length);
  }
  return null;
}

function setCookie(name, value, days) {
  let expires = '';
  if (days) {
    const d = new Date();
    d.setTime(d.getTime() + days * 86_400_000);
    expires = `; expires=${d.toUTCString()}`;
  }
  document.cookie = `${name}=${value || ''}${expires}; path=/`;
}

/* ── Country → language map (geojs translate mode) ───────────────── */

const COUNTRY_LANG = {
  AD:'ca',AL:'sq',AT:'de',BA:'bs',BE:'nl',BG:'bg',BY:'be',CH:'de',CY:'el',
  CZ:'cs',DE:'de',DK:'da',EE:'et',ES:'es',FI:'fi',FR:'fr',GB:'en',GR:'el',
  HR:'hr',HU:'hu',IE:'en',IS:'is',IT:'it',LI:'de',LT:'lt',LU:'lb',LV:'lv',
  MC:'fr',MD:'ro',ME:'sr',MK:'mk',MT:'mt',NL:'nl',NO:'no',PL:'pl',PT:'pt',
  RO:'ro',RS:'sr',RU:'ru',SE:'sv',SI:'sl',SK:'sk',SM:'it',UA:'uk',VA:'it',
  XK:'sq',US:'en',CA:'en',JP:'ja',CN:'zh',BR:'pt',MX:'es',AR:'es',AU:'en',
  IN:'hi',ZA:'en',KR:'ko',TH:'th',VN:'vi',TR:'tr',SA:'ar',AE:'ar',EG:'ar',
  IL:'he',IR:'fa',PK:'ur',BD:'bn',ID:'id',MY:'ms',SG:'en',PH:'en',NG:'en',
  KE:'sw',MA:'ar',DZ:'ar',TN:'ar',CL:'es',PE:'es',CO:'es',VE:'es',EC:'es',
  UY:'es',PY:'es',BO:'es',
};

/* ── Custom Element ──────────────────────────────────────────────── */

class GtcGeolocator extends HTMLElement {
  #shouldOpen = false;

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    this.dom = {
      form:              this.querySelector('form'),
      view:              this.querySelector('[data-view="main"]'),
      title:             this.querySelector('[data-geolocator-title]'),
      message:           this.querySelector('[data-geolocator-message]'),
      submit:            this.querySelector('[data-geolocator-submit]'),
      inputCountryCode:  this.querySelector('input[name="country_code"]'),
      inputLanguageCode: this.querySelector('input[name="language_code"]'),
      languageList:      this.querySelector('[data-language-list]'),
      languagePicker:    this.querySelector('[data-language-picker]'),
      picker:            this.querySelector('[data-picker]'),
      pickerTrigger:     this.querySelector('[data-picker-trigger]'),
      pickerLabel:       this.querySelector('[data-picker-label]'),
      dropdown:          this.querySelector('[data-dropdown]'),
      searchInput:       this.querySelector('[data-country-search]'),
    };

    this.options = Array.from(this.querySelectorAll('[data-country-option]'));

    this.settings = {
      isMarket:       this.getAttribute('is-market') === 'true',
      lang:           'en',
      cookieName:     'GTC_GEOLOCATOR',
      currentCountry: this.getAttribute('current-country') || '',
      currentLang:    this.getAttribute('current-language') || '',
      pageType:       this.getAttribute('current-page-type') || '',
      translate:      this.getAttribute('translate') || '',
      selectTitle:    this.getAttribute('select-title') || 'Select shipping location',
      defaults: {
        title:   this.dom.title?.dataset.geolocatorTitle || '',
        message: this.dom.message?.dataset.geolocatorMessage || '',
        submit:  this.dom.submit?.dataset.geolocatorSubmit || 'CONFIRM',
      },
    };

    this._selectedValue = this.settings.currentCountry;

    this._addListeners();
    this._hideLanguagePickerIfSingle();
    this._processActiveChoice();

    this._detect();
  }

  disconnectedCallback() {
    this._removeListeners();
  }

  /* ── Listeners ─────────────────────────────────────────────────── */

  _addListeners() {
    this.dom.pickerTrigger?.addEventListener('click', () => this._toggleDropdown());
    this.options.forEach(opt => {
      opt.addEventListener('click', (e) => this._onOptionClick(e));
      opt.addEventListener('focus', (e) => this._onOptionFocus(e));
    });
    this.dom.searchInput?.addEventListener('input', (e) => this._filterOptions(e.target.value));
    this.dom.searchInput?.addEventListener('focus', () => this._disableIOSZoom());
    this.dom.searchInput?.addEventListener('blur', () => this._enableIOSZoom());
    this.dom.languageList?.addEventListener('change', () => this._onLanguageChange());
    this.dom.form?.addEventListener('submit', (e) => this._onSubmit(e));
    this.dialog.addEventListener('click', (e) => this._onDialogClick(e));
    this.dialog.addEventListener('cancel', (e) => { e.preventDefault(); this.close(); });
  }

  _removeListeners() {
    /* Element removal cleans up automatically */
  }

  /* ── iOS zoom prevention ───────────────────────────────────────── */

  _disableIOSZoom() {
    const meta = document.querySelector('meta[name="viewport"]');
    if (!meta) return;
    this._originalViewport = meta.getAttribute('content');
    if (!meta.getAttribute('content').includes('maximum-scale')) {
      meta.setAttribute('content', this._originalViewport + ', maximum-scale=1');
    }
  }

  _enableIOSZoom() {
    const meta = document.querySelector('meta[name="viewport"]');
    if (!meta || !this._originalViewport) return;
    meta.setAttribute('content', this._originalViewport);
  }

  /* ── Open / Close ──────────────────────────────────────────────── */

  open() {
    if (!this.dialog) return;

    this._compact = false;

    if (this.dom.message) this.dom.message.hidden = false;

    const selected = this.querySelector('[data-country-option].is-selected');
    if (selected) this._updateText(selected);

    const sw = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.paddingRight = `${sw}px`;
    document.body.style.overflow = 'hidden';
    this.dialog.showModal();
    requestAnimationFrame(() => this.dialog.classList.add('is-open'));
  }

  openSelect() {
    this.open();
  }

  close() {
    if (!this.dialog || !this.dialog.open) return;
    this.dialog.classList.remove('is-open');
    this._closeDropdown();
    setCookie(this.settings.cookieName, this._selectedValue || this.settings.currentCountry, 30);

    const view = this.dom.view;
    const ms = view
      ? parseFloat(getComputedStyle(view).transitionDuration) * 1000 || 250
      : 0;

    setTimeout(() => {
      if (this.dialog.open) this.dialog.close();
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }, ms);
  }

  /* ── Dropdown ──────────────────────────────────────────────────── */

  _toggleDropdown() {
    const isOpen = this.dom.dropdown?.classList.contains('is-open');
    if (isOpen) {
      this._closeDropdown();
    } else {
      this._openDropdown();
    }
  }

  _openDropdown() {
    if (!this.dom.dropdown) return;
    this.dom.dropdown.classList.add('is-open');
    this.dom.pickerTrigger?.classList.add('is-active');
    this.dom.searchInput?.focus();

    const selected = this.dom.dropdown.querySelector('.is-selected');
    if (selected) selected.scrollIntoView({ block: 'nearest' });
  }

  _closeDropdown() {
    if (!this.dom.dropdown) return;
    this.dom.dropdown.classList.remove('is-open');
    this.dom.pickerTrigger?.classList.remove('is-active');
    if (this.dom.searchInput) {
      this.dom.searchInput.value = '';
      this._filterOptions('');
    }
  }

  /* ── Option selection ──────────────────────────────────────────── */

  _selectOption(btn) {
    const value = btn.dataset.value;

    this.options.forEach(o => o.classList.remove('is-selected'));
    btn.classList.add('is-selected');

    this._selectedValue = value;

    const label = btn.dataset.location
      ? `${btn.dataset.location.toUpperCase()} (${btn.dataset.currency || ''})`
      : btn.querySelector('span')?.textContent || '';
    if (this.dom.pickerLabel) this.dom.pickerLabel.textContent = label;
    if (this.dom.inputCountryCode) this.dom.inputCountryCode.value = value.toUpperCase();

    this._updateFlag(value);
    this._updateText(btn);
    this._updateLanguageList(btn.dataset.languages || '');
    this._hideLanguagePickerIfSingle();
  }

  _onOptionClick(e) {
    this._selectOption(e.currentTarget);
    this._closeDropdown();
  }

  _onOptionFocus(e) {
    this._selectOption(e.currentTarget);
  }

  _filterOptions(query) {
    const q = query.toLowerCase().trim();
    this.options.forEach(opt => {
      const text = opt.textContent.toLowerCase();
      opt.style.display = (!q || text.includes(q)) ? '' : 'none';
    });
  }

  /* ── Language picker ───────────────────────────────────────────── */

  _onLanguageChange() {
    if (this.dom.inputLanguageCode && this.dom.languageList) {
      this.dom.inputLanguageCode.value = this.dom.languageList.value;
    }
  }

  _updateLanguageList(json) {
    if (!this.dom.languageList) return;
    this.dom.languageList.innerHTML = '';

    if (json) {
      try {
        const langs = JSON.parse(json);
        const names = new Intl.DisplayNames([this.settings.lang, 'en'], { type: 'language' });

        for (const l of langs) {
          if (!l.shop_locale?.locale || !l.shop_locale.enabled || !l.shop_locale.published) continue;
          const opt = document.createElement('option');
          opt.value = l.shop_locale.locale;
          opt.textContent = names.of(l.shop_locale.locale) || l.shop_locale.locale;
          if (l.shop_locale.primary) opt.selected = true;
          this.dom.languageList.appendChild(opt);
        }
      } catch (err) {
        console.warn('Failed to parse language data:', err);
      }
    }

    this._hideLanguagePickerIfSingle();
    this._onLanguageChange();
  }

  _hideLanguagePickerIfSingle() {
    if (!this.dom.languagePicker || !this.dom.languageList) return;
    this.dom.languagePicker.style.display =
      this.dom.languageList.options.length > 1 ? '' : 'none';
  }

  /* ── Geo-detection ─────────────────────────────────────────────── */

  async _detect() {
    const params = new URLSearchParams(window.location.search);
    const forceOpen = params.has('force_geo');
    const isDesignMode = typeof Shopify !== 'undefined' && Shopify.designMode;

    if (forceOpen) {
      setCookie(this.settings.cookieName, '', -1);
      params.delete('force_geo');
      const qs = params.toString();
      window.history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : ''));
    }

    if (isDesignMode) { this.open(); return; }

    const savedCountry = getCookie(this.settings.cookieName);

    if (savedCountry == this.settings.currentCountry && !forceOpen) return;

    const detected = await this._fetchCountry();
    if (!detected) return;

    let lang = navigator.language || 'en';
    if (this.settings.translate === 'geojs') {
      lang = COUNTRY_LANG[detected.toUpperCase()] || lang;
    }
    this.settings.lang = lang;
    if (this.settings.translate) this._translateCountryNames();

    const match = this.options.find(o => o.dataset.value === detected);
    if (match) this._selectOption(match);

    if (this.settings.currentCountry === detected && !forceOpen) {
      setCookie(this.settings.cookieName, detected, 30);
      return;
    }

    const inList = this.options.find(o => o.dataset.value === savedCountry) || this.options.find(o => o.dataset.value === detected);
    if (inList) {
      this._selectOption(inList);
      this.open();
    } else {
      const intl = this.options.find(o => o.hasAttribute('data-international'));
      if (intl) {
        this._selectOption(intl);
        this.open();
      }
    }
  }

  async _fetchCountry() {
    try {
      const res = await fetch('https://get.geojs.io/v1/ip/country.json');
      const data = await res.json();
      return handleize(data.country);
    } catch {
      return '';
    }
  }

  /* ── Form submit ───────────────────────────────────────────────── */

  _processActiveChoice() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('active_choice') !== 'true') return;
    params.delete('active_choice');
    const qs = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : ''));
    setCookie(this.settings.cookieName, this.settings.currentCountry, 30);
  }

  async _onSubmit(e) {
    e.preventDefault();

    const selectedCountry = this._selectedValue;
    const selectedLang = this.settings.isMarket ? this.dom.languageList?.value : null;
    const sameCountry = selectedCountry === this.settings.currentCountry;
    const sameLang = !this.settings.isMarket || selectedLang === this.settings.currentLang;

    if (sameCountry && sameLang) {
      this.close();
      return;
    }

    this._setLoading(true);

    setCookie(this.settings.cookieName, selectedCountry, 30);

    if (this.settings.isMarket) {
      const returnTo = this.dom.form.querySelector('input[name="return_to"]');
      if (returnTo) {
        const [path, search = ''] = returnTo.value.split('?');
        const p = new URLSearchParams(search);
        p.set('active_choice', 'true');
        returnTo.value = `${path}?${p.toString()}`;
      }
      this.dom.form.submit();
    } else {
      const option = this.options.find(o => o.dataset.value === selectedCountry);
      const url = option?.dataset.url;

      if (!url) {
        this._setLoading(false);
        this.close();
        return;
      }

      const dest = new URL(url, window.location.origin);

      if (['product', 'collection'].includes(this.settings.pageType)) {
        dest.pathname = window.location.pathname.slice(1);
        try {
          const check = await fetch(`${dest}.json`);
          if (check.ok) { window.location.href = dest.href; return; }
        } catch { /* fall through */ }
      }

      window.location.href = dest.href;
    }

    setTimeout(() => { this._setLoading(false); this.close(); }, 5000);
  }

  /* ── Dialog events ─────────────────────────────────────────────── */

  _onDialogClick(e) {
    if (e.target === this.dialog) this.close();
  }

  /* ── UI helpers ────────────────────────────────────────────────── */

  _updateFlag(code) {
    document.querySelectorAll('[data-geolocator-current-flag-target]').forEach(img => {
      const p = img.src.split('/');
      p[p.length - 1] = `${code}.svg`;
      img.src = p.join('/');
    });
  }

  _updateText(option) {
    if (!option) return;

    const d = this.settings.defaults;
    const title   = option.dataset.title   || d.title;
    const message = option.dataset.message || d.message;
    const submit  = option.dataset.submit  || d.submit;
    const currency = option.dataset.currency || '';
    const location = option.dataset.location || '';

    const interpolate = (str) => str
      .replace(/\[currency\]/g, currency)
      .replace(/\[location\]/g, location);

    if (title && this.dom.title) {
      this.dom.title.textContent = interpolate(title);
    }
    if (submit && this.dom.submit) this.dom.submit.textContent = interpolate(submit);
    if (this.dom.message) {
      const lines = (message || '')
        .split('\n')
        .map(l => l.trim())
        .filter(Boolean);
      if (lines.length) {
        const ul = document.createElement('ul');
        for (const line of lines) {
          const li = document.createElement('li');
          li.textContent = interpolate(line);
          ul.appendChild(li);
        }
        this.dom.message.replaceChildren(ul);
        this.dom.message.hidden = false;
      } else {
        this.dom.message.replaceChildren();
        this.dom.message.hidden = true;
      }
    }
  }

  _translateCountryNames() {
    try {
      const names = new Intl.DisplayNames([this.settings.lang, 'en'], { type: 'region' });
      for (const opt of this.options) {
        const code = opt.dataset.value.toUpperCase();
        const translated = names.of(code);
        if (translated) {
          const span = opt.querySelector('span');
          const currency = span?.textContent.split('—')[1]?.trim() || '';
          if (span) span.textContent = currency ? `${translated} — ${currency}` : translated;
        }
      }
    } catch (err) {
      console.warn('Country translation failed:', err);
    }
  }

  _setLoading(on) {
    if (!this.dom.submit) return;
    this.dom.submit.classList.toggle('is-loading', on);
    this.dom.submit.disabled = on;
  }
}

if (!customElements.get('gtc-geolocator')) {
  customElements.define('gtc-geolocator', GtcGeolocator);
}
